import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import type { InAppNotificationListItem, TenantConversation } from '@quality-vet/contracts';

import { IconButton } from './ui-controls';
import {
  formatDate,
  isAbortError,
  readApiError,
  supersededRequestError,
} from '../utils/communication-helpers';
import './app-utility-bar.css';

export type UtilityBarMember = {
  userId: string;
  email: string;
  status?: 'invited' | 'active' | 'disabled';
};

export type AppUtilityBarProps = {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  tenantName?: string;
  currentUserId: string;
  currentUserEmail?: string;
  members?: UtilityBarMember[];
  isPlatformAdmin: boolean;
  tenantRoles?: string[];
  timeZone?: string;
  refreshKey?: number;
  onNavigateToMessages?: (conversationId?: string) => void;
  onNavigateToNotifications?: (notificationId?: string) => void;
};

type ApiRequest = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
};

const enabledMenuItemSelector = '[role="menuitem"]:not(:disabled)';

function notificationIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="M5.5 17.5h13l-1.5-2.25v-4.5a5 5 0 0 0-10 0v4.5L5.5 17.5Z" />
      <path d="M10 20h4" />
    </svg>
  );
}

function messageIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="M4.5 5.5h15v11h-9L6 20v-3.5H4.5z" />
    </svg>
  );
}

export { StatusMessage } from './status-message';

export function AppUtilityBar(props: AppUtilityBarProps) {
  const [notifications, setNotifications] = useState<InAppNotificationListItem[]>([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsDropdownOpen, setNotificationsDropdownOpen] = useState(false);
  const [conversations, setConversations] = useState<TenantConversation[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesDropdownOpen, setMessagesDropdownOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const requestGenerationRef = useRef(0);
  const requestControllersRef = useRef(new Set<AbortController>());
  const messagesMenuRef = useRef<HTMLDivElement>(null);
  const notificationsMenuRef = useRef<HTMLDivElement>(null);
  const messagesTriggerRef = useRef<HTMLButtonElement>(null);
  const notificationsTriggerRef = useRef<HTMLButtonElement>(null);
  const pendingNotificationsFocusRef = useRef<'first' | 'last' | null>(null);
  const pendingMessagesFocusRef = useRef<'first' | 'last' | null>(null);
  const notificationsMenuId = useId();
  const messagesMenuId = useId();

  useEffect(() => {
    requestGenerationRef.current += 1;
    for (const controller of requestControllersRef.current) controller.abort();
    requestControllersRef.current.clear();

    setNotificationsDropdownOpen(false);
    setNotifications([]);
    setNotificationLoading(false);
    setUnreadCount(0);
    setConversations([]);
    setMessagesLoading(false);
    setMessagesDropdownOpen(false);
    setUnreadMessages(0);

    return () => {
      requestGenerationRef.current += 1;
      for (const controller of requestControllersRef.current) controller.abort();
      requestControllersRef.current.clear();
    };
  }, [props.accessToken, props.apiUrl, props.tenantId]);

  const selectedRoles = new Set(props.tenantRoles ?? []);
  const canPublishTenantNotification =
    selectedRoles.has('tenant_admin') || selectedRoles.has('manager');

  const request = async <T,>(path: string, requestOptions: ApiRequest = {}): Promise<T> => {
    const generation = requestGenerationRef.current;
    const controller = new AbortController();
    requestControllersRef.current.add(controller);
    try {
      const response = await fetch(`${props.apiUrl}${path}`, {
        method: requestOptions.method ?? 'GET',
        headers: {
          authorization: `Bearer ${props.accessToken}`,
          ...(requestOptions.body === undefined ? {} : { 'content-type': 'application/json' }),
        },
        ...(requestOptions.body === undefined ? {} : { body: JSON.stringify(requestOptions.body) }),
        signal: controller.signal,
      });
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      if (!response.ok) {
        const message = await readApiError(response);
        if (generation !== requestGenerationRef.current) throw supersededRequestError();
        throw new Error(message);
      }
      const payload = (await response.json()) as T;
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      return payload;
    } finally {
      requestControllersRef.current.delete(controller);
    }
  };

  const tenantPath = (resource: string) =>
    `/v1/tenants/${encodeURIComponent(props.tenantId)}/${resource}`;

  const loadNotifications = async () => {
    const generation = requestGenerationRef.current;
    setNotificationLoading(true);
    try {
      if (!props.tenantId) {
        const payload = await request<{ items: InAppNotificationListItem[] }>(
          '/v1/platform/notifications',
        );
        setNotifications(payload.items);
        setUnreadCount(0);
        return;
      }
      const payload = await request<{
        items: InAppNotificationListItem[];
        unreadCount: number;
      }>(tenantPath('in-app-notifications'));
      const managementPayloads = await Promise.all([
        canPublishTenantNotification
          ? request<{ items: InAppNotificationListItem[] }>(
              tenantPath('in-app-notifications/manage'),
            )
          : Promise.resolve({ items: [] }),
        props.isPlatformAdmin
          ? request<{ items: InAppNotificationListItem[] }>('/v1/platform/notifications')
          : Promise.resolve({ items: [] }),
      ]);
      const visibleById = new Map(payload.items.map((item) => [item.id, item]));
      const combinedById = new Map<string, InAppNotificationListItem>();
      for (const item of [...managementPayloads[0].items, ...managementPayloads[1].items]) {
        combinedById.set(item.id, { ...item, ...visibleById.get(item.id) });
      }
      for (const item of payload.items) combinedById.set(item.id, item);
      setNotifications(
        [...combinedById.values()].sort((left, right) =>
          right.createdAt.localeCompare(left.createdAt),
        ),
      );
      setUnreadCount(payload.unreadCount);
    } catch (error) {
      if (isAbortError(error) || generation !== requestGenerationRef.current) return;
    } finally {
      if (generation === requestGenerationRef.current) setNotificationLoading(false);
    }
  };

  useEffect(() => {
    void loadNotifications();
  }, [props.accessToken, props.apiUrl, props.tenantId]);

  useEffect(() => {
    if (props.refreshKey !== undefined && props.refreshKey > 0) {
      void loadNotifications();
      void loadMessaging();
    }
  }, [props.refreshKey]);

  const openNotifications = (focus: 'first' | 'last' | null = null) => {
    const opening = !notificationsDropdownOpen;
    if (opening) {
      if (focus) pendingNotificationsFocusRef.current = focus;
      setMessagesDropdownOpen(false);
      void loadNotifications();
    }
    setNotificationsDropdownOpen(opening);
  };

  useEffect(() => {
    if (!notificationsDropdownOpen) return;
    if (pendingNotificationsFocusRef.current) {
      const items =
        notificationsMenuRef.current?.querySelectorAll<HTMLButtonElement>(enabledMenuItemSelector);
      const item =
        pendingNotificationsFocusRef.current === 'last'
          ? items?.item((items.length ?? 1) - 1)
          : items?.item(0);
      item?.focus();
      pendingNotificationsFocusRef.current = null;
    }
    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!notificationsMenuRef.current?.contains(event.target as Node)) {
        setNotificationsDropdownOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setNotificationsDropdownOpen(false);
        notificationsTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [notificationsDropdownOpen]);

  const handleNavigateToNotifications = (notificationId?: string) => {
    setNotificationsDropdownOpen(false);
    props.onNavigateToNotifications?.(notificationId);
  };

  const loadMessaging = async () => {
    const generation = requestGenerationRef.current;
    setMessagesLoading(true);
    try {
      const conversationPayload = props.tenantId
        ? await request<{ items: TenantConversation[] }>(tenantPath('conversations'))
        : { items: [] as TenantConversation[] };
      setConversations(conversationPayload.items);
    } catch (error) {
      if (isAbortError(error) || generation !== requestGenerationRef.current) return;
    } finally {
      if (generation === requestGenerationRef.current) setMessagesLoading(false);
    }
  };

  useEffect(() => {
    void loadMessaging();
  }, [props.accessToken, props.apiUrl, props.tenantId]);

  const openMessages = (focus: 'first' | 'last' | null = null) => {
    const opening = !messagesDropdownOpen;
    if (opening) {
      if (focus) pendingMessagesFocusRef.current = focus;
      setNotificationsDropdownOpen(false);
      void loadMessaging();
    }
    setMessagesDropdownOpen(opening);
  };

  useEffect(() => {
    if (!messagesDropdownOpen) return;
    if (pendingMessagesFocusRef.current) {
      const items =
        messagesMenuRef.current?.querySelectorAll<HTMLButtonElement>(enabledMenuItemSelector);
      const item =
        pendingMessagesFocusRef.current === 'last'
          ? items?.item((items.length ?? 1) - 1)
          : items?.item(0);
      item?.focus();
      pendingMessagesFocusRef.current = null;
    }
    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!messagesMenuRef.current?.contains(event.target as Node)) {
        setMessagesDropdownOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMessagesDropdownOpen(false);
        messagesTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [messagesDropdownOpen]);

  useEffect(() => {
    if (conversations.length === 0) {
      setUnreadMessages(0);
      return;
    }
    const lastViewedKey = `messages-last-viewed-${props.tenantId}-${props.currentUserId}`;
    const lastViewed = localStorage.getItem(lastViewedKey);
    if (!lastViewed) {
      setUnreadMessages(conversations.length > 0 ? conversations.length : 0);
      return;
    }
    const count = conversations.filter(
      (conversation) => conversation.updatedAt > lastViewed,
    ).length;
    setUnreadMessages(count);
  }, [conversations, props.tenantId, props.currentUserId]);

  const handleNavigateToMessages = (conversationId?: string) => {
    setMessagesDropdownOpen(false);
    const lastViewedKey = `messages-last-viewed-${props.tenantId}-${props.currentUserId}`;
    localStorage.setItem(lastViewedKey, new Date().toISOString());
    setUnreadMessages(0);
    props.onNavigateToMessages?.(conversationId);
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = [
      ...event.currentTarget.querySelectorAll<HTMLButtonElement>(enabledMenuItemSelector),
    ];
    if (items.length === 0) return;
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  const conversationNameForDropdown = (conversation: TenantConversation): string => {
    if (conversation.title) return conversation.title;
    const memberMap = new Map((props.members ?? []).map((m) => [m.userId, m]));
    const otherParticipants = conversation.participantUserIds.filter(
      (userId) => userId !== props.currentUserId,
    );
    return (
      otherParticipants.map((userId) => memberMap.get(userId)?.email ?? userId).join(', ') ||
      'Conversation'
    );
  };

  return (
    <>
      <nav aria-label="Communication tools" className="app-utility-bar">
        <div className="notifications-dropdown-container" ref={notificationsMenuRef}>
          <IconButton
            aria-controls={notificationsMenuId}
            aria-expanded={notificationsDropdownOpen}
            aria-haspopup="menu"
            label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
            ref={notificationsTriggerRef}
            type="button"
            onClick={() => openNotifications()}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                openNotifications('first');
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                openNotifications('last');
              } else if (
                (event.key === 'Enter' || event.key === ' ') &&
                !notificationsDropdownOpen
              ) {
                pendingNotificationsFocusRef.current = 'first';
              }
            }}
          >
            {notificationIcon()}
            {unreadCount > 0 ? (
              <span aria-hidden="true" className="utility-unread-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </IconButton>
          {notificationsDropdownOpen ? (
            <div
              aria-label="Recent notifications"
              className="notifications-dropdown-menu"
              id={notificationsMenuId}
              role="menu"
              onKeyDown={handleMenuKeyDown}
            >
              {notifications.length === 0 && !notificationLoading ? (
                <p className="notifications-dropdown-empty">No notifications yet.</p>
              ) : null}
              {notificationLoading ? (
                <p className="notifications-dropdown-empty">Loading...</p>
              ) : null}
              {[...notifications]
                .sort((left, right) => {
                  if (!left.readAt && right.readAt) return -1;
                  if (left.readAt && !right.readAt) return 1;
                  return right.createdAt.localeCompare(left.createdAt);
                })
                .slice(0, 5)
                .map((notification) => (
                  <button
                    className={`notifications-dropdown-item${notification.readAt ? '' : ' notifications-dropdown-item-unread'}`}
                    disabled={!props.onNavigateToNotifications}
                    key={notification.id}
                    role="menuitem"
                    type="button"
                    onClick={() => handleNavigateToNotifications(notification.id)}
                  >
                    <strong>{notification.title}</strong>
                    <small>{formatDate(notification.createdAt, false)}</small>
                  </button>
                ))}
              <button
                className="notifications-dropdown-footer"
                role="menuitem"
                type="button"
                onClick={() => handleNavigateToNotifications()}
              >
                {props.onNavigateToNotifications
                  ? 'All notifications'
                  : 'Navigation unavailable during setup'}
              </button>
            </div>
          ) : null}
        </div>
        <div className="messages-dropdown-container" ref={messagesMenuRef}>
          <IconButton
            aria-controls={messagesMenuId}
            aria-expanded={messagesDropdownOpen}
            aria-haspopup="menu"
            label={`Messages${unreadMessages > 0 ? `, ${unreadMessages} unread` : ''}`}
            ref={messagesTriggerRef}
            type="button"
            onClick={() => openMessages()}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                openMessages('first');
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                openMessages('last');
              } else if ((event.key === 'Enter' || event.key === ' ') && !messagesDropdownOpen) {
                pendingMessagesFocusRef.current = 'first';
              }
            }}
          >
            {messageIcon()}
            {unreadMessages > 0 ? (
              <span aria-hidden="true" className="utility-unread-badge">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            ) : null}
          </IconButton>
          {messagesDropdownOpen ? (
            <div
              aria-label="Recent messages"
              className="messages-dropdown-menu"
              id={messagesMenuId}
              role="menu"
              onKeyDown={handleMenuKeyDown}
            >
              {conversations.length === 0 && !messagesLoading ? (
                <p className="messages-dropdown-empty">No conversations yet.</p>
              ) : null}
              {messagesLoading ? <p className="messages-dropdown-empty">Loading…</p> : null}
              {conversations.slice(0, 5).map((conversation) => (
                <button
                  className="messages-dropdown-item"
                  disabled={!props.onNavigateToMessages}
                  key={conversation.id}
                  role="menuitem"
                  type="button"
                  onClick={() => handleNavigateToMessages(conversation.id)}
                >
                  <strong>{conversationNameForDropdown(conversation)}</strong>
                  <small>{formatDate(conversation.updatedAt, false)}</small>
                </button>
              ))}
              <button
                className="messages-dropdown-footer"
                role="menuitem"
                type="button"
                onClick={() => handleNavigateToMessages()}
              >
                {props.onNavigateToMessages
                  ? 'All messages'
                  : 'Navigation unavailable during setup'}
              </button>
            </div>
          ) : null}
        </div>
      </nav>
    </>
  );
}
